import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { formatDate } from "../../common/dateFormatter";

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const getAllBlogs = () => {
      axios
        .get(`${process.env.REACT_APP_BASE_URL}/blogs`)
        .then((response) => {
          const sortedBlogs = response?.data?.data.sort(
            (a, b) => a.order - b.order,
          );
          setBlogs(sortedBlogs);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    };
    getAllBlogs();
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % blogs.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + blogs.length) % blogs.length);
  };

  // Auto-advance slider
  useEffect(() => {
    if (blogs.length > 0) {
      const interval = setInterval(() => {
        nextSlide();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [blogs.length]);

  const getSlideIndex = (index) => {
    const totalSlides = blogs.length;
    return (index - currentSlide + totalSlides) % totalSlides;
  };

  const getSlideStyles = (relativeIndex) => {
    switch (relativeIndex) {
      case 0: // Center card
        return {
          transform: "translateX(0%) scale(1)",
          zIndex: 10,
          opacity: 1,
        };
      case 1: // Right card
        return {
          transform: "translateX(60%) scale(0.85)",
          zIndex: 5,
          opacity: 0.7,
        };
      case totalSlides - 1: // Left card (last when going backwards)
        return {
          transform: "translateX(-60%) scale(0.85)",
          zIndex: 5,
          opacity: 0.7,
        };
      default: // Hidden cards
        return {
          transform:
            relativeIndex < totalSlides / 2
              ? "translateX(100%) scale(0.7)"
              : "translateX(-100%) scale(0.7)",
          zIndex: 1,
          opacity: 0.3,
        };
    }
  };

  const totalSlides = blogs.length;

  if (!blogs.length) {
    return <div className="px-4 md:px-0 py-12">Loading...</div>;
  }

  return (
    <div className="px-4 md:px-0 py-12 bg-gray-50">
      {/* Header Section */}
      <div className="flex flex-col justify-between items-start md:items-center w-full md:w-[90%] m-auto mb-12">
        <div className="text-2xl md:text-3xl lg:text-[48px] leading-tight md:leading-[50px] font-[800] text-[#183864] mb-4 md:mb-0">
          Recent Blog Of Us
        </div>
        <div className="flex justify-center items-center gap-3  bg-gradient-to-r from-[#B02526] to-[#8B1E1F]  hover:from-[#8B1E1F] hover:to-[#B02526] rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform p-5 mt-4">
          <Link
            to={`/blog`}
            className="md:text-[17px] text-sm font-semibold text-white hover:underline"
          >
            See All Posts
          </Link>
          <div>
            <svg
              className="bubbling-svg w-2 h-3 md:w-[7px] md:h-[14px]"
              viewBox="0 0 7 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.73242 1.9043L6.00004 7.22249L1.73242 12.5407"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Desktop Slider */}
      <div className="hidden md:block">
        <div className="relative w-full max-w-6xl mx-auto h-[500px] flex items-center justify-center">
          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all duration-300 hover:scale-110"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-all duration-300 hover:scale-110"
          >
            <svg
              className="w-6 h-6 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          {/* Cards Container */}
          <div className="relative w-full h-full flex items-center justify-center">
            {blogs.map((item, index) => {
              const relativeIndex = getSlideIndex(index);
              const styles = getSlideStyles(relativeIndex);

              return (
                <motion.div
                  key={index}
                  className="absolute w-96 h-[450px]"
                  style={{
                    zIndex: styles.zIndex,
                  }}
                  animate={{
                    x: styles.transform.includes("translateX")
                      ? styles.transform.match(/translateX\(([^)]+)\)/)[1]
                      : "0%",
                    scale: parseFloat(
                      styles.transform.match(/scale\(([^)]+)\)/)?.[1] || 1,
                    ),
                    opacity: styles.opacity,
                  }}
                  transition={{
                    duration: 0.6,
                    ease: "easeInOut",
                  }}
                >
                  <div className="w-full h-full bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-200 hover:shadow-2xl transition-shadow duration-300">
                    {/* Image Section */}
                    <div className="relative h-52 overflow-hidden">
                      <img
                        className="w-full h-full object-cover"
                        src={`${process.env.REACT_APP_BASE_URL}/${item?.image}`}
                        alt={item?.title}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#B02526] text-white shadow-lg">
                          {item?.category || "Blog"}
                        </span>
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="p-6 h-56 flex flex-col justify-between">
                      <div>
                        {/* Date */}
                        <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                          <svg
                            className="w-4 h-4"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>
                            {formatDate(item?.date || item?.createdAt)}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-bold text-[#183864] mb-3 line-clamp-2 leading-tight">
                          {item?.title}
                        </h3>

                        {/* Description */}
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2 leading-relaxed">
                          {item?.desc}
                        </p>
                      </div>

                      {/* CTA Button */}
                      <div className="mt-auto">
                        <Link
                          to={`/blog-inside?id=${item?._id}`}
                          className="inline-flex items-center justify-center w-full bg-[#B02526] text-white px-4 py-2 rounded-lg font-semibold text-sm hover:bg-[#8B1E1F] transition-colors duration-300"
                        >
                          Read More
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Slider Indicators */}
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex justify-center space-x-2 z-30">
            {blogs.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  currentSlide === index
                    ? "bg-[#B02526] scale-125"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden">
        <div className="space-y-4">
          {blogs?.slice(0, 3)?.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300"
            >
              <div className="relative">
                <img
                  className="w-full h-48 object-cover"
                  src={`${process.env.REACT_APP_BASE_URL}/${item?.image}`}
                  alt={item?.title}
                />
                <div className="absolute top-3 right-3 bg-[#B02526] text-white px-2 py-1 rounded-full text-xs font-bold">
                  New
                </div>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-2 text-xs text-[#ADADAD] mb-2">
                  <svg
                    className="w-3 h-3"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>{formatDate(item?.date || item?.createdAt)}</span>
                </div>

                <h3 className="text-sm font-bold text-[#183864] mb-2 line-clamp-2 leading-5">
                  {item?.title}
                </h3>

                <p className="text-xs text-[#777777] mb-3 line-clamp-3 leading-4">
                  {item?.desc}
                </p>

                <Link
                  to={`/blog-inside?id=${item?._id}`}
                  className="inline-flex items-center gap-1 text-[#B02526] font-semibold text-xs hover:gap-2 transition-all duration-300"
                >
                  Read More
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Mobile Show More Button */}
        {blogs?.length > 3 && (
          <div className="text-center mt-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#B02526] to-[#d63031] text-white px-6 py-3 rounded-full font-semibold text-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105"
            >
              View All Posts
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Blog;
